import { useState, useEffect } from 'react';
import { accountsAPI, zelleAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Send, Mail, Phone, Clock, CheckCircle2, Shield, ArrowRight, Zap, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import DollarInput from '../components/DollarInput';
import PreTransferConfirm from '../components/PreTransferConfirm';
import TransferConfirmation from '../components/TransferConfirmation';

export default function ZellePage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);

  // Enrollment form
  const [enrollEmail, setEnrollEmail] = useState('');
  const [enrollPhone, setEnrollPhone] = useState('');
  const [enrollAccountId, setEnrollAccountId] = useState('');
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);

  // Send form
  const [fromAccount, setFromAccount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);
  const [preConfirmOpen, setPreConfirmOpen] = useState(false);
  const [preConfirmDetails, setPreConfirmDetails] = useState(null);

  const [recentRecipients] = useState([
    { name: 'John Smith', contact: 'john@email.com' },
    { name: 'Sarah Johnson', contact: '(555) 123-4567' },
    { name: 'Mike Williams', contact: 'mike@email.com' },
  ]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accRes, enrollRes] = await Promise.all([
        accountsAPI.list(),
        zelleAPI.enrollment()
      ]);
      setAccounts(accRes.data.accounts);
      setEnrolled(enrollRes.data.enrolled);
      setEnrollment(enrollRes.data.enrollment);

      if (accRes.data.accounts.length > 0) {
        setFromAccount(accRes.data.accounts[0]._id);
        setEnrollAccountId(accRes.data.accounts[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!enrollEmail && !enrollPhone) {
      toast.error('Please provide an email address or phone number');
      return;
    }
    if (!enrollAccountId) {
      toast.error('Please select a linked account');
      return;
    }
    setEnrollSubmitting(true);
    try {
      const res = await zelleAPI.enroll({
        email: enrollEmail,
        phone: enrollPhone,
        linked_account_id: enrollAccountId
      });
      toast.success('Successfully enrolled in Zelle!');
      setEnrolled(true);
      setEnrollment(res.data.enrollment);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrollSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!recipient) { toast.error('Enter recipient email or phone'); return; }
    setPreConfirmDetails({
      type: 'Zelle Payment',
      amount: parseFloat(amount),
      fromAccount: selectedAccount?.name || 'Account',
      recipient: recipient,
    });
    setPreConfirmOpen(true);
  };

  const executeZelleSend = async () => {
    setLoading(true);
    try {
      await zelleAPI.send({
        from_account_id: fromAccount,
        recipient,
        amount: parseFloat(amount),
        note
      });
      setPreConfirmOpen(false);
      setConfirmDetails({
        type: 'Zelle Payment',
        amount: parseFloat(amount),
        recipient: recipient,
        fromAccount: selectedAccount?.name || 'Account',
      });
      setConfirmOpen(true);
      setRecipient(''); setAmount(''); setNote('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zelle payment failed');
    } finally { setLoading(false); }
  };

  const selectedAccount = accounts.find(a => a._id === fromAccount);

  if (enrollmentLoading) {
    return <div className="page-container"><p>Loading Zelle...</p></div>;
  }

  // ==================== ENROLLMENT SCREEN ====================
  if (!enrolled) {
    return (
      <div className="page-container">
        <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Zelle</h2>

        <div className="max-w-2xl mx-auto">
          {/* Enrollment Hero */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-8 text-white text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Send className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Enroll in Zelle</h3>
              <p className="text-purple-100 text-sm max-w-md mx-auto">
                Send and receive money fast with just an email address or U.S. mobile number. No fees.
              </p>
            </div>
          </Card>

          {/* Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <Zap className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-semibold">Fast</p>
                <p className="text-xs text-muted-foreground mt-1">Money moves in minutes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Shield className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-semibold">Secure</p>
                <p className="text-xs text-muted-foreground mt-1">Bank-level security</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <CheckCircle2 className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-semibold">Free</p>
                <p className="text-xs text-muted-foreground mt-1">No fees to send or receive</p>
              </CardContent>
            </Card>
          </div>

          {/* Enrollment Form */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Set Up Your Zelle Profile</CardTitle>
                  <p className="text-sm text-muted-foreground">Register your email or phone to get started</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEnroll} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="zelle-enroll-email-input"
                      className="pl-10"
                      placeholder="your.email@example.com"
                      value={enrollEmail}
                      onChange={e => setEnrollEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="relative flex items-center justify-center">
                  <Separator className="flex-1" />
                  <span className="px-3 text-xs text-muted-foreground bg-white">or</span>
                  <Separator className="flex-1" />
                </div>

                <div className="space-y-2">
                  <Label>U.S. Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="zelle-enroll-phone-input"
                      className="pl-10"
                      placeholder="(555) 123-4567"
                      value={enrollPhone}
                      onChange={e => setEnrollPhone(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Link to Account</Label>
                  <Select value={enrollAccountId} onValueChange={setEnrollAccountId}>
                    <SelectTrigger data-testid="zelle-enroll-account-select">
                      <SelectValue placeholder="Select account to link" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => (
                        <SelectItem key={a._id} value={a._id}>
                          {a.name} ({formatCurrency(a.available_balance)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">This account will be used to send and receive Zelle payments</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground text-sm">By enrolling, you agree to:</p>
                  <p>• Zelle Network Terms of Service</p>
                  <p>• Mega Millions Reserve Escrow Bank's digital payment policy</p>
                  <p>• Allow recipients to send you money using the registered email/phone</p>
                </div>

                <Button data-testid="zelle-enroll-submit-button" type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={enrollSubmitting}>
                  {enrollSubmitting ? (
                    'Enrolling...'
                  ) : (
                    <>
                      Enroll in Zelle <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ==================== SEND MONEY SCREEN (after enrollment) ====================
  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Zelle</h2>

      {/* Enrollment status bar */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm font-medium text-green-800">Zelle Enrolled</p>
            <p className="text-xs text-green-600">
              {enrollment?.email && <><Mail className="h-3 w-3 inline mr-1" />{enrollment.email}</>}
              {enrollment?.email && enrollment?.phone && ' · '}
              {enrollment?.phone && <><Phone className="h-3 w-3 inline mr-1" />{enrollment.phone}</>}
            </p>
          </div>
        </div>
        <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Send className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Send Money with Zelle</CardTitle>
                <p className="text-sm text-muted-foreground">Send money instantly using email or phone number</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>From Account</Label>
                <Select value={fromAccount} onValueChange={setFromAccount}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(a => (
                      <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedAccount && <p className="text-xs text-muted-foreground">Available: {formatCurrency(selectedAccount.available_balance)}</p>}
              </div>
              <div className="space-y-2">
                <Label>Recipient (Email or Phone)</Label>
                <Input data-testid="zelle-recipient-input" placeholder="email@example.com or (555) 123-4567" value={recipient} onChange={e => setRecipient(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <DollarInput data-testid="zelle-amount-input" value={amount} onChange={e => setAmount(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Note (Optional)</Label>
                <Textarea placeholder="What's this for?" value={note} onChange={e => setNote(e.target.value)} />
              </div>
              <Button data-testid="zelle-send-button" type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Review & Send'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recent Recipients */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Recent Recipients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentRecipients.map((r, i) => (
                <button
                  key={i}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left transition-colors"
                  onClick={() => setRecipient(r.contact)}
                >
                  <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-purple-700">{r.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {r.contact.includes('@') ? <Mail className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
                      {r.contact}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>Money typically arrives in minutes</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <PreTransferConfirm open={preConfirmOpen} onClose={setPreConfirmOpen} onConfirm={executeZelleSend} details={preConfirmDetails} loading={loading} />
      <TransferConfirmation open={confirmOpen} onClose={setConfirmOpen} transferDetails={confirmDetails} />
    </div>
  );
}
